import { useCallback, useEffect, useRef, useState } from 'react';
import {
  POST_H,
  POST_W,
  canvasToShareFile,
  composeStoryShareCanvasFromNode,
  runStoryShareFlow,
} from 'utils/storyShare';

/**
 * Pipeline share riusabile: cattura nodo → canvas (Stories 9:16 o Post 1:1) → share flow.
 * Prefetch dopo mount così il 1° tap su mobile tiene il gesto utente per navigator.share.
 * `format`: 'story' | 'post'
 */
export const useStoryShare = ({
  fileBaseName = 'zetabarber-story',
  format = 'story',
  isMobile,
  onToast,
  prefetch = false,
}) => {
  const captureRef = useRef(null);
  const cacheRef = useRef(null);
  const promiseRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const [ready, setReady] = useState(false);

  const resetCache = useCallback(() => {
    cacheRef.current = null;
    promiseRef.current = null;
    setReady(false);
  }, []);

  useEffect(() => {
    resetCache();
  }, [fileBaseName, format, resetCache]);

  const ensureFile = useCallback(() => {
    if (cacheRef.current) {
      setReady(true);
      return Promise.resolve(cacheRef.current);
    }
    if (promiseRef.current) return promiseRef.current;
    if (!captureRef.current) return Promise.reject(new Error('Capture missing'));

    const options =
      format === 'post' ? { outWidth: POST_W, outHeight: POST_H, pad: 0.08 } : undefined;

    promiseRef.current = composeStoryShareCanvasFromNode(captureRef.current, options)
      .then((canvas) => canvasToShareFile(canvas, fileBaseName))
      .then((file) => {
        cacheRef.current = file;
        setReady(true);
        return file;
      })
      .catch((err) => {
        promiseRef.current = null;
        setReady(false);
        throw err;
      });

    return promiseRef.current;
  }, [fileBaseName, format]);

  // Prefetch in idle: al tap il file è spesso già pronto → share sheet al 1° gesto
  useEffect(() => {
    if (!prefetch) return undefined;
    let cancelled = false;
    const run = () => {
      if (cancelled || !captureRef.current) return;
      ensureFile().catch(() => {});
    };
    const idleId =
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback(run, { timeout: 1200 })
        : null;
    const timeoutId = window.setTimeout(run, idleId == null ? 500 : 1500);
    return () => {
      cancelled = true;
      if (idleId != null && typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(idleId);
      }
      window.clearTimeout(timeoutId);
    };
  }, [ensureFile, prefetch]);

  const share = useCallback(async () => {
    if (sharing) return;
    try {
      setSharing(true);
      const file = await ensureFile();
      await runStoryShareFlow({
        file,
        isMobile,
        onToast,
      });
    } catch (err) {
      console.warn('Advertising story share failed', err);
      onToast?.('error');
    } finally {
      setSharing(false);
    }
  }, [ensureFile, isMobile, onToast, sharing]);

  return { captureRef, ensureFile, share, sharing, ready, resetCache };
};
